import { memo, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Grid2, Box, CircularProgress, Collapse, Button, TextField, InputAdornment } from '@mui/material';
import { useConfirm } from "../../contexts/confirmDialogContext";
import FullScreenActionLoader from '../common/FullScreenActionLoader';
import { GenericConstant } from "../../utils/GenericConstant";
import { DataGridPro } from '@mui/x-data-grid-pro';
import type { GridPaginationModel, GridCellParams, GridSortModel, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useCommonStyles } from '../../styles/GenericStyle';
import { useResponsiveHeaderHeight } from '../../hooks/useResponsiveHeaderHeight';
import { useGetArchivedTrials, useGetTrials, getMaterialsByTrial, useGetMaterialByTrial, useSearchTrialsByFilters, useGetPaginatedTrialsByStatus, useGetHighPriorityTrials, exportTrialMaterialsCsv, useSearchBoxTrial } from '../../api/vegApl';
import { getTrialsGridCols } from "./trailGridConfigs";
import { useCommonTrailStyles, kpiToggleButtonSx, getBaseDataGridSx, RESPONSIVE_GRID_MAX_HEIGHT } from "./trailStyles";
import { useExportToExcel, buildTrialMaterialsExportData, removeCsvColumns } from "../../utils/ExcelExport";
import { PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from "./trialConstants";
import type { TrialData, PaginationModel, TrailDataGridProps } from "./trailInterface";
import { useGlobalState } from "../../contexts/GlobalState";
import TrailMaterials from "./trailMaterials";
import { useSearchParams } from 'react-router-dom';
import KPI from '../common/kpi';
import DashboardHeader from "../common/DashboardHeader";
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import barchartIcon from '/icons/barchart_icon.png';
import type { ListValueTrail } from '../../model/commonType';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import MarkTrialCompletedPopup from '../popup/marktrialcompletedpopup';
import { useSessionState } from '../../hooks/useSessionState';
import { usePersistedGridState } from '../../hooks/usePersistedGridState';
import {checkArray } from '../../utils/HelperFunction';
import { getTrialStatusIdsByTab, resolveAccessibleFilterByStatusIds } from '../../utils/statusUtils';
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import { FloatingActionBar } from '../spLot/FloatingBar';
import { useSnackbar } from '../../contexts/SnackbarContext';
import SearchIcon from "@mui/icons-material/Search";
import { MtComStyleObj } from '../../styles/MtComStyleObj';

function TrailDataGrid({ hidden, showArchivedTrials = false, showKPI: initialShowKPI = true, activeTab }: TrailDataGridProps) {
    const classes = useCommonStyles();
    const headerHeight = useResponsiveHeaderHeight(48);
    const trailClasses = useCommonTrailStyles();
    const { exportToExcel } = useExportToExcel();
    const { showSnackbar } = useSnackbar();
    const { confirm } = useConfirm();
    const { crops, trialStatuses, trialRemarks, priorities, state: { siteSelected, roleSelected, role } = {} } = useGlobalState();
    const canMarkComplete = useMemo(() => {
        const roleName = roleSelected || role?.[0]?.role_name;
        return roleName === 'S2S-Lead' || roleName === 'Admin';
    }, [roleSelected, role]);
    const isFetchingRef = useRef(false);
    const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSearchingRef = useRef(false);  // Track if active search is in progress
    const [isLoading, setIsLoading] = useState(false);
    const [trialGridData, setTrialGridData] = useState<TrialData[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [searchTrial, setSearchTrial] = useState("");
    const [isFilterVisible, setFilterVisible] = useState(false);
    const [isGlobalSearchActive, setIsGlobalSearchActive] = useState(false);
    const [paginationModel, setPaginationModel] = useState<PaginationModel>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [toggleMaterialDetails, setToggleMaterialDetails] = useSessionState('trial_toggleMaterialDetails', true);
    const [selectedTrialData, setSelectedTrialData] = useSessionState<TrialData | null>('trial_selectedTrialData', null);
    // Bumped every time a drilldown is opened, so its filter panel always remounts fresh
    // instead of keeping a stale Material ID from a previous visit to the same trial.
    const [drilldownVisitId, setDrilldownVisitId] = useState(0);
    const TRIAL_ACCESSIBLE_FILTERS = ['In Progress', 'Ready for Harvest', 'Completed', 'High priority MatIDs'];
    const [accessiblefilters] = useState<string[]>(TRIAL_ACCESSIBLE_FILTERS);
    const {
        filterValues, selectedFilter, activeFilter,
        setFilterValues, setSelectedFilter, setActiveFilter,
    } = usePersistedFilters({
        moduleName: 'trial',
        defaultFilter: 'In Progress',
        validFilters: TRIAL_ACCESSIBLE_FILTERS,
        isArchived: showArchivedTrials,
        persistState: true,
    });
    const hasInitializedTrialAccessibleFilter = useRef(false);
    const selectedFilterRef = useRef(selectedFilter);
    const skipNextFilterPanelRefreshRef = useRef(false);
    const [hasUnsavedMaterialChanges, setHasUnsavedMaterialChanges] = useState(false);
    const [showKPI, setShowKPI] = useState(initialShowKPI);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'last_date', sort: 'desc' }]);
    const [openMarkTrialPopup, setOpenMarkTrialPopup] = useState(false);
    const [markTrialPopupLoading, setMarkTrialPopupLoading] = useState(false);
    const [selectedTrialBidForCompletion, setSelectedTrialBidForCompletion] = useState('');
    const [selectedTrialStatusForCompletion, setSelectedTrialStatusForCompletion] = useState('');
    const [markTrialMaterials, setMarkTrialMaterials] = useState<any>({
        total_materials: 0,
        status_breakdown: {},
    });
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

    const { mutate: fetchMarkTrialMaterials } = useGetMaterialByTrial(
        { trial_bid: selectedTrialBidForCompletion },
        {
            mutation: {
                onSuccess: (data: any) => {
                    const materialsData = (data?.data?.totals as any) ?? {
                        total_materials: 0,
                        status_breakdown: {},
                    };
                    setMarkTrialMaterials(materialsData);
                    setMarkTrialPopupLoading(false);
                },
                onError: () => {
                    setMarkTrialMaterials({
                        total_materials: 0,
                        status_breakdown: {},
                    });
                    setMarkTrialPopupLoading(false);
                },
            },
        }
    );
    const [searchParams] = useSearchParams();
    const trialBidFromQuery = searchParams.get('trialBid')?.trim() ?? '';
    const openedQueryTrialBidRef = useRef<string | null>(null);
    const apiRef = useGridApiRef();
    const { applyColumnWidths, onColumnWidthChange } = usePersistedGridState('trial-main-grid');
    const [listValues, setListValue] = useState<ListValueTrail>({
        crops: [],
        trialStatus: []
    });

    const trialGridDataWithIds = useMemo(() =>
        trialGridData.map((r, i) => ({ ...r, _uniqueId: `${r.trial_bid}_${i}` })),
        [trialGridData]);

    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    }, []);

    const handleSetFilterValues = useCallback((data: any[]) => {
        if (isGlobalSearchActive) return;
        setFilterValues(data);
    }, [setFilterValues, isGlobalSearchActive]);

    const resolveAccessibleTrialFilter = useCallback((statusIds: any): string => {
        return resolveAccessibleFilterByStatusIds({
            statusIds,
            filters: TRIAL_ACCESSIBLE_FILTERS,
            excludedFilters: ['High priority MatIDs'],
            getIdsForFilter: (filter) => getTrialStatusIdsByTab(filter, trialStatuses),
        });
    }, [trialStatuses, TRIAL_ACCESSIBLE_FILTERS]);

    const handleFilterApplied = useCallback((filterData: any | null) => {
        setIsGlobalSearchActive(false);
        setActiveFilter(filterData);
        const mappedFilter = resolveAccessibleTrialFilter(filterData?.trial_status_id);
        if (selectedFilterRef.current !== mappedFilter) {
            selectedFilterRef.current = mappedFilter;
            setSelectedFilter(mappedFilter);
        }
    }, [setActiveFilter, resolveAccessibleTrialFilter, setSelectedFilter]);

    const handleUnsavedChangesUpdate = useCallback((hasChanges: boolean) => {
        setHasUnsavedMaterialChanges(hasChanges);
    }, []);

    useEffect(() => {
        selectedFilterRef.current = selectedFilter;
    }, [selectedFilter]);

    // Shared handler for when grid data arrives (from any source)
    const handleSetGridData = useCallback((data: any) => {
        const safeRows = Array.isArray(data) ? data : [];
        setTrialGridData(safeRows);
        setIsLoading(false);
        isFetchingRef.current = false;
        if (fetchTimeoutRef.current) { clearTimeout(fetchTimeoutRef.current); fetchTimeoutRef.current = null; }
    }, []);

    // Ref to track whether last search was a filter+search combo (for setGridData callback selection)
    const isFilterSearchActiveRef = useRef(false);

    // Wrapper for filter API setGridData (already has isLoading clear via handleSetGridData)
    const handleSetGridDataWithLoadingClear = useCallback((data: any) => {
        handleSetGridData(data);
    }, [handleSetGridData]);

    const trialMutationSuccess = useCallback((data: any) => {
        const trials = data?.data?.trial_data ?? [];
        setTotalRows(data?.data?.pagination?.total_records ?? 0);
        handleSetGridData(trials);
    }, [handleSetGridData]);

    const trialMutationError = useCallback(() => {
        setTotalRows(0);
        handleSetGridData([]);
    }, [handleSetGridData]);

    const { mutate: getTrials } = useGetTrials({
        mutation: {
            onSuccess: trialMutationSuccess,
            onError: trialMutationError
        }
    });

    const { mutate: getInProgressTrials } = useGetPaginatedTrialsByStatus({
        mutation: {
            onSuccess: trialMutationSuccess,
            onError: trialMutationError
        }
    });

    const { mutate: getReadyForHarvestTrials } = useGetPaginatedTrialsByStatus({
        mutation: {
            onSuccess: trialMutationSuccess,
            onError: trialMutationError
        }
    });

    const { mutate: getCompletedTrials } = useGetPaginatedTrialsByStatus({
        mutation: {
            onSuccess: trialMutationSuccess,
            onError: trialMutationError
        }
    });

    const { mutate: getArchivedTrials } = useGetArchivedTrials({
        mutation: {
            onSuccess: trialMutationSuccess,
            onError: trialMutationError
        }
    });

    const { mutate: getHighPriorityTrials } = useGetHighPriorityTrials({
        mutation: {
            onSuccess: trialMutationSuccess,
            onError: trialMutationError
        }
    });

    const { mutate: searchByFilters } = useSearchTrialsByFilters({
        mutation: {
            onSuccess: trialMutationSuccess,
            onError: trialMutationError
        }
    });

    const { mutate: searchBoxTrial } = useSearchBoxTrial({
        mutation: {
            onSuccess: (data: any) => {
                isSearchingRef.current = false;  // Clear search flag
                const trials = Array.isArray(data?.data)
                    ? data.data
                    : (data?.data?.trial_data ?? []);
                setTotalRows(data?.data?.pagination?.total_records ?? (Array.isArray(trials) ? trials.length : 0));
                handleSetGridData(trials);
            },
            onError: (error: any) => {
                isSearchingRef.current = false;  // Clear search flag
                setTotalRows(0);
                handleSetGridData([]);
                showSnackbar({ message: error?.response?.data?.detail || "Error searching trial", severity: 'error' });
            }
        }
    });

    const getStatusIdsForTab = useCallback((tab: string): number[] => {
        return getTrialStatusIdsByTab(tab, trialStatuses);
    }, [trialStatuses]);

    const trialFilterFetchers: Record<string, (vars: { model: any; siteCode?: string }) => void> = useMemo(() => ({
        'In Progress': ({ model, siteCode }) => getInProgressTrials({ data: { status_id: getStatusIdsForTab('In Progress'), site_code: siteCode ?? '', page: model.page, page_size: model.pageSize } }),
        'Ready for Harvest': ({ model, siteCode }) => getReadyForHarvestTrials({ data: { status_id: getStatusIdsForTab('Ready for Harvest'), site_code: siteCode ?? '', page: model.page, page_size: model.pageSize } }),
        'Completed': ({ model, siteCode }) => getCompletedTrials({ data: { status_id: getStatusIdsForTab('Completed'), site_code: siteCode ?? '', page: model.page, page_size: model.pageSize } }),
        'High priority MatIDs': getHighPriorityTrials,
    }), [getInProgressTrials, getReadyForHarvestTrials, getCompletedTrials, getHighPriorityTrials, getStatusIdsForTab]);

    // Unified fetch helper - handles lock, timeout, and loading state
    const startFetch = useCallback((model: PaginationModel, fetchFn: () => void) => {
        // Prevent fetching while a global search is in progress
        if (isSearchingRef.current) return false;
        if (!siteSelected || isFetchingRef.current) return false;
        isFetchingRef.current = true;
        if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = setTimeout(() => {
            isFetchingRef.current = false;
            fetchTimeoutRef.current = null;
        }, 30000);
        setIsLoading(true);
        setPaginationModel(model);
        fetchFn();
        return true;
    }, [siteSelected]);

    const fetchTrialsByFilter = useCallback((model: PaginationModel, filter?: string) => {
        const resolvedFilter = filter ?? selectedFilterRef.current;
        const fetcher = trialFilterFetchers[resolvedFilter] ?? getTrials;
        startFetch(model, () => fetcher({ model, siteCode: siteSelected }));
    }, [getTrials, trialFilterFetchers, siteSelected, startFetch]);

    const fetchTrials = useCallback((model: PaginationModel, isArchived: boolean = false) => {
        if (isArchived) {
            startFetch(model, () => getArchivedTrials({ model, siteCode: siteSelected }));
        } else {
            fetchTrialsByFilter(model);
        }
    }, [getArchivedTrials, fetchTrialsByFilter, siteSelected, startFetch]);

    const updatePaginationModel = useCallback((model: GridPaginationModel) => {
        if (!siteSelected) return;
        // Skip if model hasn't actually changed (prevents loops from grid auto-corrections)
        // DataGrid sends 0-based page; convert to 1-based for internal use
        const internalPage = model.page + 1;
        if (internalPage === paginationModel.page && model.pageSize === paginationModel.pageSize) return;

        // In archived view, always use the archived API — ignore any active filter
        if (showArchivedTrials) {
            fetchTrials({ page: paginationModel.pageSize !== model.pageSize ? 1 : internalPage, pageSize: model.pageSize }, true);
            return;
        }

        if (activeFilter) {
            if (isFetchingRef.current) return;
            isFetchingRef.current = true;
            setIsLoading(true);
            const newPage = paginationModel.pageSize !== model.pageSize ? 1 : internalPage;
            setPaginationModel({ page: newPage, pageSize: model.pageSize });
            // Guard: always enforce the current tab's status IDs so pagination never
            // shows data from a different tab (e.g. Completed rows in In Progress tab).
            const tabStatusIds = getStatusIdsForTab(selectedFilterRef.current);
            const requestBody = {
                ...activeFilter,
                ...(tabStatusIds.length > 0 ? { trial_status_id: tabStatusIds } : {}),
                site_code: siteSelected,
                page: newPage,
                page_size: model.pageSize
            };
            searchByFilters({ requestBody });
        } else {
            fetchTrials({ page: paginationModel.pageSize !== model.pageSize ? 1 : internalPage, pageSize: model.pageSize }, false);
        }
    }, [fetchTrials, showArchivedTrials, activeFilter, searchByFilters, setPaginationModel, paginationModel]);

    const closeFloater = useCallback(() => {
        setSelectionModel([]);
        if (typeof apiRef.current?.setRowSelectionModel === 'function') {
            apiRef.current.setRowSelectionModel([]);
        }
    }, [apiRef]);

    const onAccessibleFilterChange = useCallback((filter: string) => {
        if (selectedFilterRef.current === filter) return;

        skipNextFilterPanelRefreshRef.current = true;
        setIsGlobalSearchActive(false);
        closeFloater();
        setActiveFilter(null, filter);
        isFetchingRef.current = false;
        selectedFilterRef.current = filter;
        setPaginationModel({ page: 1, pageSize: paginationModel.pageSize });
        setSelectedFilter(filter);
        fetchTrialsByFilter({ page: 1, pageSize: paginationModel.pageSize }, filter);
    }, [setSelectedFilter, setActiveFilter, paginationModel.pageSize, closeFloater, fetchTrialsByFilter]);

    // On initial load of Trials dashboard, force the accessible filter to In Progress
    // so selected pill and grid data are aligned.
    useEffect(() => {
        // Don't proceed if a search is in progress
        if (isSearchingRef.current) return;
        
        if (hasInitializedTrialAccessibleFilter.current) return;
        if (showArchivedTrials || !siteSelected || activeTab !== 2) return;

        hasInitializedTrialAccessibleFilter.current = true;
        selectedFilterRef.current = 'In Progress';

        if (selectedFilter !== 'In Progress') {
            setSelectedFilter('In Progress');
        }
        setActiveFilter(null, 'In Progress');
        setPaginationModel({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
        fetchTrialsByFilter({ page: 1, pageSize: DEFAULT_PAGE_SIZE }, 'In Progress');
    }, [
        showArchivedTrials,
        siteSelected,
        activeTab,
        selectedFilter,
        setSelectedFilter,
        setActiveFilter,
        fetchTrialsByFilter,
    ]);

    const handleTrialSearch = useCallback(() => {
        const trimmedSearch = searchTrial?.trim();
        // If search field is cleared, restore previous records
        if (!trimmedSearch) {
            isSearchingRef.current = false;
            setIsGlobalSearchActive(false);
            setFilterValues([]);
            selectedFilterRef.current = 'In Progress';
            setSelectedFilter('In Progress');
            setActiveFilter(null, 'In Progress');
            setPaginationModel({ page: 1, pageSize: paginationModel.pageSize });
            fetchTrialsByFilter({ page: 1, pageSize: paginationModel.pageSize }, 'In Progress');
            return;
        }

        // Set search flag to prevent other API calls
        isSearchingRef.current = true;
        
        // Set loading state first to clear previous data
        setIsLoading(true);
        
        // Global search should ignore filters and accessible filter tabs.
        const activeFilterTab = selectedFilterRef.current || 'In Progress';
        setIsGlobalSearchActive(true);
        setFilterValues([]);
        setActiveFilter(null, activeFilterTab);
        setSelectedFilter('');
        
        // Clear pagination model to prevent cache issues
        setPaginationModel({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
        
        // Clear previous grid data to show fresh state
        setTrialGridData([]);
        setTotalRows(0);
        
        // Always use global search API regardless of filter state.
        isFilterSearchActiveRef.current = false;
        searchBoxTrial({ trial_id: trimmedSearch, page: 1, page_size: DEFAULT_PAGE_SIZE, is_archive: showArchivedTrials });
    }, [searchTrial, searchBoxTrial, fetchTrialsByFilter, showArchivedTrials, paginationModel.pageSize, setFilterValues, setActiveFilter, setSelectedFilter]);

    const handleOnCellClick = useCallback((params: GridCellParams<TrialData>, event: any) => {
        if (showArchivedTrials) return;

        const field = String(params?.field ?? '');
        if (field === '__check__' || field === '') return;

        const selection = window.getSelection?.()?.toString()?.trim();
        if (selection) return;

        const target = event?.target as HTMLElement | null;
        if (target?.closest('a, button, [role="button"], .MuiDataGrid-actionsCell')) return;

        setToggleMaterialDetails(false);
        setSelectedTrialData(params.row || null);
        setDrilldownVisitId(prev => prev + 1);
    }, [showArchivedTrials]);

    const handleOpenTrialDetailsFromLink = useCallback((row: TrialData) => {
        if (!row) return;
        setToggleMaterialDetails(false);
        setSelectedTrialData(row);
        setDrilldownVisitId(prev => prev + 1);
    }, []);

    useEffect(() => {
        // Don't proceed if a search is in progress
        if (isSearchingRef.current) return;
        
        if (hidden || showArchivedTrials || !trialBidFromQuery) return;
        if (openedQueryTrialBidRef.current === trialBidFromQuery) return;

        // Wait until grid data has loaded before opening drilldown
        const matchedRow = trialGridData.find((r) => r.trial_bid === trialBidFromQuery);
        const rowToUse: TrialData = matchedRow ?? {
            trial_bid: trialBidFromQuery,
            crop: '',
            trial_status: '',
            local_code: '',
            sector: '',
            matid_completion: 0,
            start_date: '',
            end_date: '',
        };

        openedQueryTrialBidRef.current = trialBidFromQuery;
        setSelectedTrialData(rowToUse);
        setToggleMaterialDetails(false);
    }, [hidden, showArchivedTrials, trialBidFromQuery, trialGridData]);

    const downloadTrialMaterials = useCallback(async (trial_bid: string, totalMaterials?: number) => {
        try {
            const pageSize = Math.max(Number(totalMaterials) || DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE);
            const response = await getMaterialsByTrial({ trial_bid, page: 0, pageSize });
            const materials = (response?.data as any)?.materials ?? [];
            if (materials.length === 0) {
                console.warn('No materials found for trial:', trial_bid);
                return;
            }
            const exportData = buildTrialMaterialsExportData(materials, undefined, {
                trialRemarks,
                priorities,
            });
            exportToExcel(exportData, `TrialMaterials_${trial_bid}`, ['isModified', 'material_id', 'status_id', '_rowUid', '_rowType', '_hasChildren', '_childRows']);
        } catch (error) {
            console.error('Failed to download trial materials:', error);
        }
    }, [exportToExcel, trialRemarks, priorities]);

    const handleBulkDownloadTrialsCsv = useCallback(async () => {
        try {
            const selectedRows = selectionModel
                .map((rowId) => trialGridDataWithIds.find((r: any) => r._uniqueId === rowId))
                .filter(Boolean);

            const trialIds = Array.from(new Set(selectedRows.map((r: any) => String(r.trial_bid ?? '')).filter(Boolean)));
            if (trialIds.length === 0) {
                showSnackbar({ message: 'No trials selected for download', severity: 'info' });
                return;
            }

            const response = await exportTrialMaterialsCsv({ trial_ids: trialIds });
            const rawCsvContent = response?.data instanceof Blob
                ? await response.data.text()
                : String(response?.data ?? '');
            const csvContent = removeCsvColumns(rawCsvContent, ['SAPNR', 'sapnr']);
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });


            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const _now = new Date();
            const _dateStr = String(_now.getDate()).padStart(2, '0') + String(_now.getMonth() + 1).padStart(2, '0') + _now.getFullYear();
            link.download = `trial-materials-${_dateStr}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            closeFloater();
        } catch (error: any) {
            console.error('Failed to export selected trial materials CSV:', error);
            showSnackbar({ message: error?.response?.data?.detail || 'Failed to download selected trials CSV', severity: 'error' });
        }
    }, [selectionModel, trialGridDataWithIds, showSnackbar, closeFloater]);

    const handleOpenMarkTrialPopup = useCallback((trialBid: string, trialStatus?: string) => {
        setSelectedTrialBidForCompletion(trialBid);
        setSelectedTrialStatusForCompletion(trialStatus ?? '');
        setMarkTrialMaterials({
            total_materials: 0,
            status_breakdown: {},
        });
        setOpenMarkTrialPopup(true);
        setMarkTrialPopupLoading(true);
    }, []);

    useEffect(() => {
        if (!selectedTrialBidForCompletion || !openMarkTrialPopup) return;
        fetchMarkTrialMaterials({});
    }, [selectedTrialBidForCompletion, openMarkTrialPopup]);

    const handleMarkTrialCompletedSuccess = useCallback(() => {
        if (!siteSelected) return;
        const currentModel = { page: paginationModel.page, pageSize: paginationModel.pageSize };
        isFetchingRef.current = false;

        if (showArchivedTrials) {
            fetchTrials(currentModel, true);
            return;
        }

        if (activeFilter) {
            // Guard: always enforce the current tab's status IDs so the refresh after
            // marking a trial complete doesn't show the just-completed trial in In Progress.
            const tabStatusIds = getStatusIdsForTab(selectedFilterRef.current);
            startFetch(currentModel, () => searchByFilters({
                requestBody: {
                    ...activeFilter,
                    ...(tabStatusIds.length > 0 ? { trial_status_id: tabStatusIds } : {}),
                    site_code: siteSelected,
                    page: currentModel.page,
                    page_size: currentModel.pageSize,
                }
            }));
            return;
        }

        fetchTrialsByFilter(currentModel, selectedFilter);
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        showArchivedTrials,
        fetchTrials,
        activeFilter,
        searchByFilters,
        fetchTrialsByFilter,
        selectedFilter,
        siteSelected,
        startFetch,
        getStatusIdsForTab,
    ]);

    useEffect(() => {
        setListValue((prev) => ({ ...prev, crops: crops, trialStatus: trialStatuses }));
    }, [crops, trialStatuses]);

    useEffect(() => {
        closeFloater();
    }, [activeTab, closeFloater]);

    const isInitialLoadRef = useRef(true);
    const prevSiteRef = useRef<string | undefined>(siteSelected);
    const prevArchivedRef = useRef(showArchivedTrials);

    // Unified effect: handles site changes and archived toggle
    // NOTE: Initial load is handled by GenericFilterPanel which calls onRefreshData
    useEffect(() => {
        // Don't proceed if a search is in progress
        if (isSearchingRef.current) return;
        
        if (!siteSelected) return;

        const isInitialLoad = isInitialLoadRef.current;
        const siteChanged = prevSiteRef.current !== siteSelected && !isInitialLoad;
        const archivedToggled = prevArchivedRef.current !== showArchivedTrials && !isInitialLoad;

        // Update refs for next render
        prevSiteRef.current = siteSelected;
        prevArchivedRef.current = showArchivedTrials;

        // Skip initial load - GenericFilterPanel will handle it via onRefreshData
        if (isInitialLoad) {
            isInitialLoadRef.current = false;
            setIsLoading(true); // Show loading until GenericFilterPanel triggers fetch
            return;
        }

        // Only proceed if site changed or archived toggled
        if (!siteChanged && !archivedToggled) return;

        const executeFetch = async () => {
            // Handle unsaved changes confirmation when toggling views.
            // No direct API calls here — GenericFilterPanel will trigger onRefreshData
            // (fetchTrials) with the correct archived flag, eliminating duplicate calls.
            // Site changed — go back to trials dashboard from drilldown view
            if (siteChanged && !toggleMaterialDetails) {
                setToggleMaterialDetails(true);
                setSelectedTrialData(null);
            }

            if (archivedToggled && !toggleMaterialDetails && hasUnsavedMaterialChanges) {
                const ok: any = await confirm({
                    title: GenericConstant.CONFIRMATION,
                    description: GenericConstant.UNSAVECHANGEPOPOP,
                    confirmText: "Yes",
                    cancelText: "No",
                    severity: "error",
                });
                if (!ok) return;
                setToggleMaterialDetails(true);
                setSelectedTrialData(null);
                setHasUnsavedMaterialChanges(false);
            } else if (archivedToggled && !toggleMaterialDetails) {
                setToggleMaterialDetails(true);
                setSelectedTrialData(null);
            }

            // Clear stale fetch lock and safety timeout; reset pagination on transition.
            isFetchingRef.current = false;
            if (fetchTimeoutRef.current) { clearTimeout(fetchTimeoutRef.current); fetchTimeoutRef.current = null; }
            setPaginationModel({ page: 1, pageSize: paginationModel.pageSize });
        };

        executeFetch();
    }, [siteSelected, showArchivedTrials]);



//     useEffect(() => {

//         console.log("aa")
//         //const id = requestAnimationFrame(() => {
         
// setTimeout(() => {
//      apiRef?.current?.resize?.();
// },500)  
            
//   apiRef.current?.autosizeColumns({
//       expand: true,
//       includeHeaders: true,
   
//     });
//     }, [hidden]);





    return (
        <>
            <Grid2 size={{ xs: 12, sm: 12, md: 12 }} sx={{ flexDirection: 'column', display: (!hidden && toggleMaterialDetails) ? 'flex' : 'none', px: { xs: 1, sm: 2, md: 0 }, overflow: 'hidden' }}>
                <DashboardHeader
                    title="Trials Dashboard"
                    accessibleFilters={accessiblefilters}
                    selectedFilter={selectedFilter}
                    onFilterChange={onAccessibleFilterChange}
                    isFilterVisible={isFilterVisible}
                    setFilterVisible={setFilterVisible}
                    showFilters={!showArchivedTrials}
                    setFilterFieldData={handleSetFilterValues}
                    filterValues={isGlobalSearchActive ? [] : filterValues}
                    isDashboardHeaderVisiBle={activeTab == 2}
                    filterPanelProps={{
                        moduleName: 'trial',
                        listValues: listValues,
                        setGridData: isFilterSearchActiveRef.current ? handleSetGridDataWithLoadingClear : handleSetGridData,
                        showArchived: showArchivedTrials,
                        persistState: true,
                        onRefreshData: () => {
                            if (skipNextFilterPanelRefreshRef.current) {
                                skipNextFilterPanelRefreshRef.current = false;
                                return;
                            }
                            fetchTrials({ page: 1, pageSize: DEFAULT_PAGE_SIZE }, showArchivedTrials);
                        },
                        setTotalRows: setTotalRows,
                        paginationModel: paginationModel,
                        onFilterApplied: handleFilterApplied,
                        accessibleFilterTab: selectedFilter
                    }}
                    searchSlot={
                        <>
                            <TextField
                                placeholder="Enter Trial, Material, Pollination, Container, or SP Lot ID"
                                autoFocus
                                variant="outlined"
                                value={searchTrial}
                                onChange={(e) => {
                                    const nextValue = e.target.value;
                                    setSearchTrial(nextValue);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleTrialSearch();
                                    }
                                }}
                                sx={(theme) => ({
                                    ...MtComStyleObj(theme).textFocusHoverStyle,
                                    ...MtComStyleObj(theme).textBoxValueStyle,
                                    backgroundColor: '#FFFFFF',
                                    minWidth: { xs: 200, sm: 300, md: 420, lg: 480 },
                                })}
                                slotProps={{
                                    input: {
                                        sx: { height: 40 },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleTrialSearch}
                                disableRipple
                                sx={(theme) => ({
                                    ...MtComStyleObj(theme).disabledGreenBtnStyle,
                                    padding: '10px',
                                })}
                                className={classes.greenButton}
                            >
                                Search
                            </Button>
                        </>
                    }
                    actionButtonsSlot={<></>}
                    kpiToggleButton={activeTab === 2 && !showArchivedTrials && !showKPI ? (
                        <Button
                            onClick={() => setShowKPI((prev) => !prev)}
                            variant="outlined"
                            disableRipple
                            startIcon={
                                <Box component="img" src={barchartIcon} alt="Bar chart" sx={{ width: 16, height: 16 }} />
                            }
                            sx={kpiToggleButtonSx}
                        >
                            Show KPIs
                        </Button>
                    ) : undefined}
                    kpiContent={activeTab === 2 && !showArchivedTrials ? (
                        <Collapse
                            in={showKPI}
                            timeout={{ enter: 420, exit: 260 }}
                            easing={{ enter: 'cubic-bezier(0.22, 1, 0.36, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
                            sx={{
                                width: '100%',
                                overflow: 'hidden',
                                '& .MuiCollapse-wrapperInner': {
                                    opacity: showKPI ? 1 : 0,
                                    transform: showKPI ? 'translateY(0px)' : 'translateY(-8px)',
                                    transition: 'opacity 320ms ease, transform 320ms ease',
                                },
                            }}
                        >
                            <KPI
                                kpiSection='trails'
                                hideButton={
                                    <Button
                                        onClick={() => setShowKPI((prev) => !prev)}
                                        variant="outlined"
                                        disableRipple
                                        startIcon={<VisibilityOffOutlinedIcon fontSize="small" />}
                                        sx={kpiToggleButtonSx}
                                    >
                                        Hide KPIs
                                    </Button>
                                }
                            />
                        </Collapse>
                    ) : undefined}
                />

                <FullScreenActionLoader open={isLoading} />
                <Box sx={{ minHeight: 0, width: '100%', mt: { xs: 1, sm: 2 }, flex: 1, overflow: 'hidden' }}>
                    {!hidden && isLoading ? (
                        <Box sx={{ display: 'flex', placeItems: 'center', justifyContent: 'center', height: '100%', p: { xs: 3, sm: 5 } }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                     !hidden && toggleMaterialDetails && (
                        <Box sx={{ width: '100%'}}>
                            <DataGridPro
                                pageSizeOptions={PAGE_SIZE_OPTIONS}
                                columns={applyColumnWidths(getTrialsGridCols(classes, trailClasses, downloadTrialMaterials, handleOpenMarkTrialPopup, showArchivedTrials, sortModel, canMarkComplete, handleOpenTrialDetailsFromLink))}                              
                                rows={checkArray(trialGridDataWithIds)} 
                                columnHeaderHeight={headerHeight}
                                getRowHeight={() => 'auto'}
                                disableRowSelectionOnClick
                                checkboxSelection
                                getRowId={(r) => r._uniqueId}
                                pagination
                                paginationMode="server"
                                rowCount={totalRows}
                                paginationModel={{ ...paginationModel, page: paginationModel.page - 1 }}
                                onPaginationModelChange={updatePaginationModel}
                                onRowSelectionModelChange={(newModel) => setSelectionModel(newModel)}
                                sortModel={sortModel}
                                onSortModelChange={handleSortModelChange}
                                onColumnWidthChange={onColumnWidthChange}
                                onCellClick={handleOnCellClick}
                                disableColumnSelector={showArchivedTrials}
                                disableColumnMenu={showArchivedTrials}
                              


                                localeText={{
                                    noRowsLabel: showArchivedTrials ? 'No Archived Trials' : 'No Trials found',
                                    noResultsOverlayLabel: showArchivedTrials ? 'No Archived Trials' : 'No Trials found',
                                }}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 50, page: 0 } },
                                }}
                                apiRef={apiRef}
                                slotProps={{
                                    baseCheckbox: {
                                        checkedIcon: <Box component="img" alt="" src="icons/CheckBox.svg" />,
                                        icon: <CheckBoxOutlineBlankRoundedIcon fontSize="small" />,
                                        sx: {
                                            "&.Mui-checked": { color: '#000000' },
                                            color: '#000000',
                                            fontSize: 20,
                                            "& svg": { fontSize: 20 },
                                        },
                                    },
                                }}
                                sx={(theme) => ({
                                    ...getBaseDataGridSx(theme),
                                    '& .MuiDataGrid-main': {
                                        maxHeight: RESPONSIVE_GRID_MAX_HEIGHT,
                                        overflow: 'auto',
                                        width: '100%'
                                    },
                                    ...(showArchivedTrials && {
                                        opacity: 0.85,
                                        pointerEvents: 'auto',
                                        '& .MuiDataGrid-row': { cursor: 'default' },
                                    }),
                                })}
                            />
                        </Box>)
                    )}
                </Box>

                <FloatingActionBar
                    open={selectionModel.length > 1}
                    selectedCount={selectionModel.length}
                    onExport={handleBulkDownloadTrialsCsv}
                    onClose={closeFloater}
                    type="trial"
                    hideActions
                />

            </Grid2>

            <TrailMaterials toggleMaterialDetails={toggleMaterialDetails} hidden={hidden} data={selectedTrialData ?? {} as TrialData} setToggleMaterialDetails={setToggleMaterialDetails} exportToExcel={exportToExcel} onUnsavedChangesUpdate={handleUnsavedChangesUpdate} activeTab={activeTab} isArchived={showArchivedTrials} drilldownVisitId={drilldownVisitId} />
            <MarkTrialCompletedPopup
                open={openMarkTrialPopup}
                onClose={() => setOpenMarkTrialPopup(false)}
                onCompleted={handleMarkTrialCompletedSuccess}
                trialBid={selectedTrialBidForCompletion}
                trialStatus={selectedTrialStatusForCompletion}
                materials={markTrialMaterials}
                isLoading={markTrialPopupLoading}
            />

        </>
    );
}

export default memo(TrailDataGrid);
